import { useState, useEffect, useContext, useCallback, useMemo, Fragment } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AuthContext } from '../../utils/auth.js';
import ModalCustom from '../../components/utils/ModalCustom.js';
import ShowUserDetails from './ShowUserDetails.js';

export default function UserJobStats({ users = {} }) {
    const { currentUser } = useContext(AuthContext);
    const serverUrl = process.env.REACT_APP_SERVER_URL;

    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortKey, setSortKey] = useState('total');
    const [sortDir, setSortDir] = useState('desc');
    const [expandedUserId, setExpandedUserId] = useState(null);

    const [selectedUserId, setSelectedUserId] = useState('');
    const [detailsOpen, setDetailsOpen] = useState(false);

    const [resettingUserId, setResettingUserId] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm.trim().toLowerCase()), 250);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchStats = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const idToken = await currentUser.getIdToken();
            const response = await axios.get(`${serverUrl}/admin/getUserJobStats`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${idToken}`
                },
                timeout: 30000
            });
            setData(response.data);
            setLastUpdated(new Date());
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to fetch user job stats');
        } finally {
            setIsLoading(false);
        }
    }, [currentUser, serverUrl]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const handleResetUser = useCallback(async (userId, emailForPrompt) => {
        const label = emailForPrompt || userId;
        if (!window.confirm(`Reset all active jobs for ${label}?`)) return;

        setResettingUserId(userId);
        const toastId = toast.loading(`Resetting active jobs for ${label}...`);

        try {
            const idToken = await currentUser.getIdToken();
            const response = await axios.post(
                `${serverUrl}/admin/resetActiveJobs`,
                { userId },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${idToken}`
                    },
                    timeout: 30000
                }
            );

            const { resetCount = 0, failedCount = 0 } = response.data || {};
            if (failedCount > 0) {
                toast.error(`Reset ${resetCount} job(s), ${failedCount} failed`, { id: toastId });
            } else if (resetCount === 0) {
                toast.success('No stuck jobs to reset', { id: toastId });
            } else {
                toast.success(`Reset ${resetCount} job(s)`, { id: toastId });
            }

            await fetchStats();
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to reset jobs';
            toast.error(msg, { id: toastId });
        } finally {
            setResettingUserId(null);
        }
    }, [currentUser, serverUrl, fetchStats]);

    const enrichedUsers = useMemo(() => {
        if (!data?.users) return [];
        return data.users.map(u => ({
            ...u,
            email: users[u.userId] || u.email || u.userId,
            total: u.total ?? ((u.active || 0) + (u.waiting || 0) + (u.delayed || 0)),
            active: u.active || 0,
            waiting: u.waiting || 0,
            delayed: u.delayed || 0,
            completed: u.completed || 0,
            failed: u.failed || 0,
        }));
    }, [data, users]);

    const filteredSortedUsers = useMemo(() => {
        let list = enrichedUsers;

        if (debouncedSearch) {
            list = list.filter(u =>
                (u.email || '').toLowerCase().includes(debouncedSearch) ||
                (u.userId || '').toLowerCase().includes(debouncedSearch)
            );
        }

        list = [...list].sort((a, b) => {
            const aVal = a[sortKey];
            const bVal = b[sortKey];
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            const aNum = aVal ?? 0;
            const bNum = bVal ?? 0;
            return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
        });

        return list;
    }, [enrichedUsers, debouncedSearch, sortKey, sortDir]);

    const totals = data?.totals || (() => {
        const t = { users: 0, active: 0, waiting: 0, delayed: 0, completed: 0, failed: 0, total: 0 };
        enrichedUsers.forEach(u => {
            t.users += 1;
            t.active += u.active;
            t.waiting += u.waiting;
            t.delayed += u.delayed;
            t.completed += u.completed;
            t.failed += u.failed;
            t.total += u.total;
        });
        return t;
    })();

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    const sortIndicator = (key) => {
        if (sortKey !== key) return null;
        return <span className="ml-1 text-slate-400">{sortDir === 'asc' ? '▲' : '▼'}</span>;
    };

    const openUserDetails = (userId) => {
        setSelectedUserId(userId);
        setDetailsOpen(true);
    };

    const formatQueueName = (name) => {
        return name
            .replace('gpt-queue', 'GPT')
            .replace('azure-queue', 'Azure')
            .replace('edenAI-queue', 'EdenAI')
            .replace('document-classification-azure-queue', 'Classification Azure')
            .replace('document-classification-gpt-queue', 'Classification GPT')
            .replace(/-/g, ' ');
    };

    const headerCell = (key, label, align = 'right') => (
        <th
            onClick={() => handleSort(key)}
            className={`px-3 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer select-none hover:text-slate-900 ${align === 'right' ? 'text-right' : 'text-left'}`}
        >
            {label}{sortIndicator(key)}
        </th>
    );

    return (
        <div className="space-y-6 pt-4">
            <ModalCustom open={detailsOpen} setOpen={setDetailsOpen}>
                <ShowUserDetails
                    userId={selectedUserId}
                    closeModal={() => {
                        setDetailsOpen(false);
                        setSelectedUserId('');
                    }}
                />
            </ModalCustom>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 shadow-lg">
                <div>
                    <h2 className="text-2xl font-bold text-white">User Job Stats</h2>
                    <p className="text-slate-400 text-sm mt-1">
                        Worker jobs aggregated per user across all queues
                    </p>
                    {lastUpdated && (
                        <p className="text-slate-500 text-xs mt-2">
                            Last updated: {lastUpdated.toLocaleTimeString()}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={fetchStats}
                        disabled={isLoading}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${isLoading
                            ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                            : 'bg-indigo-600 text-white hover:bg-indigo-500 active:scale-95'
                            }`}
                    >
                        {isLoading ? (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                        ) : (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        )}
                        {isLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {/* Error state */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-red-700 font-medium">{error}</span>
                    </div>
                </div>
            )}

            {/* Totals overview */}
            {(data || enrichedUsers.length > 0) && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-slate-800">{totals.users}</div>
                        <div className="text-sm text-slate-500 mt-1">Users</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-slate-800">{totals.total}</div>
                        <div className="text-sm text-slate-500 mt-1">Total Pending</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-emerald-600">{totals.active}</div>
                        <div className="text-sm text-slate-500 mt-1">Active</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-amber-600">{totals.waiting}</div>
                        <div className="text-sm text-slate-500 mt-1">Waiting</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-sky-600">{totals.delayed}</div>
                        <div className="text-sm text-slate-500 mt-1">Delayed</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-green-600">{totals.completed}</div>
                        <div className="text-sm text-slate-500 mt-1">Completed</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                        <div className="text-3xl font-bold text-red-600">{totals.failed}</div>
                        <div className="text-sm text-slate-500 mt-1">Failed</div>
                    </div>
                </div>
            )}

            {/* Search */}
            {(data || enrichedUsers.length > 0) && (
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex items-center gap-3">
                    <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by email or user ID..."
                        className="flex-1 outline-none text-sm text-slate-700 placeholder-slate-400 bg-transparent"
                    />
                    <span className="text-xs text-slate-400 whitespace-nowrap">
                        {filteredSortedUsers.length} of {enrichedUsers.length} users
                    </span>
                </div>
            )}

            {/* Users table */}
            {filteredSortedUsers.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                            <thead className="bg-slate-50">
                                <tr>
                                    {headerCell('email', 'User', 'left')}
                                    {headerCell('total', 'Pending')}
                                    {headerCell('active', 'Active')}
                                    {headerCell('waiting', 'Waiting')}
                                    {headerCell('delayed', 'Delayed')}
                                    {headerCell('completed', 'Completed')}
                                    {headerCell('failed', 'Failed')}
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredSortedUsers.map((u) => {
                                    const isExpanded = expandedUserId === u.userId;
                                    const queueEntries = u.queues ? Object.entries(u.queues) : [];

                                    return (
                                        <Fragment key={u.userId}>
                                            <tr className="hover:bg-slate-50 transition-colors">
                                                <td className="px-3 py-3 text-left">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-2 h-2 rounded-full ${u.active > 0 ? 'bg-emerald-500' : u.waiting > 0 ? 'bg-amber-500' : 'bg-slate-300'}`} />
                                                        <button
                                                            onClick={() => openUserDetails(u.userId)}
                                                            className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                                                            title="Open user details"
                                                        >
                                                            {u.email}
                                                        </button>
                                                    </div>
                                                    <div className="text-xs text-slate-400 mt-0.5 ml-4 font-mono truncate max-w-xs" title={u.userId}>
                                                        {u.userId}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3 text-right text-sm font-semibold text-slate-800">{u.total}</td>
                                                <td className="px-3 py-3 text-right text-sm font-semibold text-emerald-600">{u.active}</td>
                                                <td className="px-3 py-3 text-right text-sm font-semibold text-amber-600">{u.waiting}</td>
                                                <td className="px-3 py-3 text-right text-sm font-semibold text-sky-600">{u.delayed}</td>
                                                <td className="px-3 py-3 text-right text-sm font-semibold text-green-600">{u.completed}</td>
                                                <td className="px-3 py-3 text-right text-sm font-semibold text-red-600">{u.failed}</td>
                                                <td className="px-3 py-3 text-right">
                                                    <div className="inline-flex items-center gap-1 justify-end">
                                                        <button
                                                            onClick={() => handleResetUser(u.userId, u.email)}
                                                            disabled={resettingUserId === u.userId || u.active === 0}
                                                            title={u.active === 0 ? 'No active jobs to reset' : 'Move all active jobs back to waiting'}
                                                            className={`text-xs px-2 py-1 rounded border transition-colors ${u.active === 0 || resettingUserId === u.userId
                                                                ? 'border-slate-200 text-slate-300 cursor-not-allowed'
                                                                : 'border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300'
                                                                }`}
                                                        >
                                                            {resettingUserId === u.userId ? 'Resetting...' : 'Reset stuck'}
                                                        </button>
                                                        {queueEntries.length > 0 && (
                                                            <button
                                                                onClick={() => setExpandedUserId(isExpanded ? null : u.userId)}
                                                                className="text-xs text-slate-500 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-100"
                                                            >
                                                                {isExpanded ? 'Hide' : 'Details'}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && queueEntries.length > 0 && (
                                                <tr className="bg-slate-50">
                                                    <td colSpan={8} className="px-6 py-4">
                                                        <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Per-Queue Breakdown</div>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                                            {queueEntries.map(([queueName, q]) => (
                                                                <div key={queueName} className="bg-white rounded-lg p-3 border border-slate-100">
                                                                    <div className="text-sm font-medium text-slate-700 mb-2">{formatQueueName(queueName)}</div>
                                                                    <div className="grid grid-cols-5 gap-2 text-xs">
                                                                        <div className="text-center">
                                                                            <div className="font-semibold text-emerald-600">{q.active || 0}</div>
                                                                            <div className="text-slate-400">Active</div>
                                                                        </div>
                                                                        <div className="text-center">
                                                                            <div className="font-semibold text-amber-600">{q.waiting || 0}</div>
                                                                            <div className="text-slate-400">Wait</div>
                                                                        </div>
                                                                        <div className="text-center">
                                                                            <div className="font-semibold text-sky-600">{q.delayed || 0}</div>
                                                                            <div className="text-slate-400">Delay</div>
                                                                        </div>
                                                                        <div className="text-center">
                                                                            <div className="font-semibold text-green-600">{q.completed || 0}</div>
                                                                            <div className="text-slate-400">Done</div>
                                                                        </div>
                                                                        <div className="text-center">
                                                                            <div className="font-semibold text-red-600">{q.failed || 0}</div>
                                                                            <div className="text-slate-400">Fail</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Loading skeleton */}
            {isLoading && !data && (
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 animate-pulse">
                            <div className="h-6 bg-slate-200 rounded w-1/4 mb-4" />
                            <div className="space-y-3">
                                <div className="h-4 bg-slate-100 rounded w-full" />
                                <div className="h-4 bg-slate-100 rounded w-3/4" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty state */}
            {!isLoading && !error && enrichedUsers.length === 0 && (
                <div className="bg-white rounded-xl p-12 shadow-sm border border-slate-100 text-center">
                    <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-7a4 4 0 11-8 0 4 4 0 018 0zm6 4a3 3 0 100-6 3 3 0 000 6z" />
                    </svg>
                    <h3 className="mt-4 text-lg font-medium text-slate-900">No user job data available</h3>
                    <p className="mt-2 text-sm text-slate-500">Try refreshing to fetch the latest user job stats.</p>
                </div>
            )}
        </div>
    );
}
